import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { IPaymentResult, IStudentPayment } from '../../../../models/payments.model';
import { IPaymentUnit } from './PaymentProcessingMain';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Checkbox, Select, Tooltip } from 'antd';
import useUtil from '../../../../hooks/useUtil';
import { formatAmount, getDateFormat, getInstallment, removeComma } from '../../../../lib/util';
import { IPaymentAmount, ISelectedCoupon } from '../../../../models/studentStudent.model';
import useNotification from '../../../../hooks/useNotification';
import ShuttleAmountModal from '../modals/ShuttleAmountModal';
import { getPaymentStatus } from '../../../../api/nicePayments.api';
import useAxios from '../../../../hooks/useAxios';
import _ from 'lodash';
import usePayment from '../../../../hooks/usePayment';
import { IStudent } from '../../../../models/students.model';
import Button from '../../../Button';
import SpecialClassAmountModal from '../modals/SpecialClassAmountModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import { PAYMENT_EDIT_PERMISSIONS } from '../../../../constans';
import PaymentAmountModal from '../modals/PaymentAmountModal';

const Block = styled.div`
    .month-even {
        background-color: lemonchiffon;
    }
    
    .item-pay.pr-disabled {
        opacity: 0.8;
        background-color: #fff5f5;
        border: 1px solid #ffcccc;
        position: relative;
        cursor: help;
        
        &:hover {
            background-color: #ffe5e5;
        }
    }
`;

interface IProps {
  data: IStudentPayment[];
  paymentUnit: IPaymentUnit;
  paymentRecords: IStudentPayment[];
  setPaymentRecords: (records: IStudentPayment[]) => void;
  paymentAmounts: IPaymentAmount[];
  setPaymentAmounts: (amounts: IPaymentAmount[]) => void;
  paymentResults: IPaymentResult[];
  setPaymentResults: (results: IPaymentResult[]) => void;
  selectedCoupons: ISelectedCoupon[];
  setSelectedCoupons: (couponRecords: ISelectedCoupon[]) => void;
  doGetStudentPayments: () => void;
  student?: IStudent;
  paymentInstallments: { paymentNo: number; value: string }[];
  setPaymentInstallments: (results: { paymentNo: number; value: string }[]) => void;
  paymentStatus: { paymentNo: number, status: 'RE' | 'SU' | 'FA' | 'PR' }[];
  prStatusPayments: number[];
  setPrStatusPayments: (payments: number[]) => void;
}

const PaymentProcessingTable: FC<IProps> = ({
                                              data,
                                              paymentUnit,
                                              paymentRecords,
                                              setPaymentRecords,
                                              paymentAmounts,
                                              setPaymentAmounts,
                                              paymentResults,
                                              setPaymentResults,
                                              selectedCoupons,
                                              setSelectedCoupons,
                                              doGetStudentPayments,
                                              student,
                                              paymentInstallments,
                                              setPaymentInstallments,
                                              paymentStatus,
                                              prStatusPayments: prStatusPaymentsFromProps,
                                              setPrStatusPayments,
                                            }) => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const { getCommonCodeLabel } = useUtil();
  const {
    getPaymentAmount,
    getMaxAmount,
    getCouponDiscount,
    refineFirstPaymentItems,
    refineSecondPaymentItems,
  } = usePayment();

  const [shuttleAmountVisible, setShuttleAmountVisible] = useState(false);
  const [specialClassAmountVisible, setSpecialClassAmountVisible] = useState(false);
  const [paymentAmountVisible, setPaymentAmountVisible] = useState(false);
  const [paymentRecord, setPaymentRecord] = useState<IStudentPayment>();
  const [isFirstRun, setIsFirstRun] = useState(true);
  
  // 부모에서 받은 prStatusPayments를 로컬 상태와 동기화
  const prStatusPayments = prStatusPaymentsFromProps;
  const { execute: statusExecute } = useAxios<any>();

  const selectInstallment: string[] = ['일시불', ...getInstallment()];
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);

  // data에 있는 결제건들의 PR 상태 확인
  useEffect(() => {
    const checkPaymentsPRStatus = async () => {
      if (!data || data.length === 0) return;

      const prPayments: number[] = [];

      // data에 있는 paymentNo들만 확인
      for (const payment of data) {
        if (payment.paymentNo) {
          try {
            const response = await getPaymentStatus(payment.paymentNo);
            console.log('결제 상태 확인:', payment.paymentNo, response.data);

            // status 필드 확인 (history가 아니라 status)
            const responseData = response.data?.data;
            const status = responseData?.status;

            console.log('상태 값:', status);

            // status가 PR이면 목록에 추가
            if (status === 'PR') {
              prPayments.push(payment.paymentNo);
              console.log(`PR 상태 감지: paymentNo ${payment.paymentNo}`);
            }
          } catch (error) {
            console.error('결제 상태 확인 실패:', payment.paymentNo, error);
          }
        }
      }

      // PR 상태인 paymentNo들 저장
      console.log('최종 PR 상태 결제건들:', prPayments);
      console.log('prPayments 길이:', prPayments.length);
      setPrStatusPayments(prPayments);
    };

    checkPaymentsPRStatus();
  }, [data]); // data 변경 시 재실행

  // prStatusPayments 상태 변경 감지
  useEffect(() => {
    console.log('prStatusPayments 상태 업데이트됨:', prStatusPayments);
  }, [prStatusPayments]);

  useEffect(() => {
    const newPaymentRecords: IStudentPayment[] = [];
    const newPaymentAmounts: IPaymentAmount[] = [];
    for (let i = 0; i < data.length; i++) {
      newPaymentAmounts.push({
        paymentNo: data[i].paymentNo,
        amount: getMinAmount(
          data[i].price -
          data[i].discountAmount -
          data[i].paymentAmount,
        ),
        price: data[i].price,
        paymentTargetCode: data[i].paymentTargetCode,
        installmentPlanMonth: data[i].installmentPlanMonth,
        checked: false,
      });
    }
    const month = data[0].monthSeq;
    //여러학기 결제건이 있는경우 monthseq + monthName 기준으로 체크박스 체크
    const chkSemesterName = data[0].semesterName;
    data.forEach((item, index) => {
      if (item.monthSeq === month && item.semesterName === chkSemesterName) {
        newPaymentRecords.push(item);
        newPaymentAmounts[index].checked = true;
      }
    });
    findAndSetPaymentResult(newPaymentAmounts);
    setPaymentAmounts(newPaymentAmounts);
    setPaymentRecords(newPaymentRecords);
  }, [data]);

  useEffect(() => {
    if (!data.length || !paymentAmounts.length || isFirstRun) {
      return;
    }
    if (paymentUnit === 'month') {
      const newPaymentRecords: IStudentPayment[] = [];
      const newPaymentAmounts: IPaymentAmount[] = [];
      paymentAmounts.forEach(item => {
        newPaymentAmounts.push({
          ...item,
          checked: false,
        });
      });
      const month = data.find(item => item.monthSeq)?.monthSeq || 0;
      //여러학기 결제건이 있는경우 monthseq + monthName 기준으로 체크박스 체크
      const chkSemesterName = data.find(item => item.monthSeq)?.semesterName;
      data.forEach((item, index) => {
        if ((item.monthSeq === month && item.semesterName === chkSemesterName) || item.monthSeq === 0) {
          newPaymentRecords.push(item);
          newPaymentAmounts[index].checked = true;
        }
      });
      findAndSetPaymentResult(newPaymentAmounts);
      setPaymentAmounts(newPaymentAmounts);
      setPaymentRecords(newPaymentRecords);
    } else {
      const newPaymentAmounts: IPaymentAmount[] = [];
      data.forEach((item, index) => {
        newPaymentAmounts.push({
          ...paymentAmounts[index],
          checked: true,
        });
      });
      findAndSetPaymentResult(newPaymentAmounts);
      setPaymentAmounts(newPaymentAmounts);
      setPaymentRecords([...data]);
    }
  }, [paymentUnit]);

  useEffect(() => {
    if (isFirstRun) {
      setIsFirstRun(false);
      return;
    }
    const newPaymentAmounts = paymentAmounts.map(item => ({
      ...item,
      checked: false,
    }));
    for (const paymentRecord of paymentRecords) {
      const index = data.findIndex(item => item.paymentNo === paymentRecord.paymentNo);
      if (index > -1) {
        newPaymentAmounts[index].checked = true;
      }
    }
    findAndSetPaymentResult(newPaymentAmounts);
    setPaymentAmounts(newPaymentAmounts);
  }, [paymentRecords]);

  useEffect(() => {
    if (selectedCoupons.length <= 0 || paymentAmounts.length <= 0) {
      return;
    }

    const newPaymentAmounts = [...paymentAmounts];

    data.forEach((item, index) => {
      const previousDiscount = getCouponDiscount(item, selectedCoupons);
      const totalPrice = item.price - item.discountAmount - item.paymentAmount - previousDiscount;

      if (previousDiscount === 0) {
        newPaymentAmounts[index] = {
          ...newPaymentAmounts[index],
          amount: getMinAmount(item.price),
        };
      } else {
        newPaymentAmounts[index] = {
          ...newPaymentAmounts[index],
          amount: getMinAmount(totalPrice),
        };
      }

      if (totalPrice <= 50000) {
        handleInstallmentChange(item.paymentNo);
      }
    });

    findAndSetPaymentResult(newPaymentAmounts);
    setPaymentAmounts(newPaymentAmounts);

  }, [selectedCoupons]);


  const getDiscountAmount = (discountAmount: number, index: number) => {
    return (
      discountAmount +
      getCouponDiscount(data[index], selectedCoupons)
    );
  };

  const getMinAmount = (couponAmount: number) => {
    return couponAmount <= 0 ? 0 : couponAmount;
  };

  const getUnpaidAmountMin = (
    paymentRecord: IStudentPayment,
    totalPaymentAmount: number,
    index: number,
  ) => {
    return getMinAmount(
      paymentRecord.price -
      paymentRecord.discountAmount -
      paymentRecord.paymentAmount -
      totalPaymentAmount -
      getCouponDiscount(data[index], selectedCoupons),
    );
  };

  const getUnpaidAmount = (
    paymentRecord: IStudentPayment,
    totalPaymentAmount: number,
    index: number,
  ) => {
    return paymentRecord.price -
      paymentRecord.discountAmount -
      paymentRecord.paymentAmount -
      totalPaymentAmount -
      getCouponDiscount(data[index], selectedCoupons);

  };

  const findAndSetPaymentResult = (paymentAmounts: IPaymentAmount[]) => {
    const findPaymentStateIndex = paymentResults.findIndex(
      ({ label }) => label === '상태',
    );
    if (findPaymentStateIndex > -1) {
      const newPaymentResults = [...paymentResults];
      const firstPaymentTuition = getPaymentAmount(paymentAmounts, refineFirstPaymentItems);
      const secondPaymentTuition = getPaymentAmount(paymentAmounts, refineSecondPaymentItems);
      newPaymentResults[findPaymentStateIndex] = {
        ...newPaymentResults[findPaymentStateIndex],
        firstPaymentTuition: firstPaymentTuition <= 0 ? 'N / A' : '결제 대기',
        secondPaymentTuition: secondPaymentTuition <= 0 ? 'N / A' : '결제 대기',
      };
      setPaymentResults(newPaymentResults);
    }
  };

  const totalPaymentAmountsChange = (
    e: ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const num = +removeComma(e.target.value);
    if (isNaN(num)) {
      return e.preventDefault();
    }
    if (getUnpaidAmount(data[index], num, index) < 0) {
      e.preventDefault();
      return infoNotification(t('student.student.message33'));
    }

    const newPaymentAmounts = [...paymentAmounts];
    newPaymentAmounts[index] = { ...newPaymentAmounts[index], amount: num };
    findAndSetPaymentResult(newPaymentAmounts);
    setPaymentAmounts(newPaymentAmounts);
  };

  const isPaymentRecordChecked = (paymentNo: number) => {
    return paymentRecords.some((record) => record.paymentNo === paymentNo);
  };

  const togglePaymentRecord = (selectedRecord: IStudentPayment) => {
    if (isPaymentRecordChecked(selectedRecord.paymentNo)) {
      const filteredRecords = paymentRecords.filter(
        item => {
          return (
            item.monthSeq > selectedRecord.monthSeq &&
            item.paymentTargetCode === selectedRecord.paymentTargetCode
          );
        },
      );
      if (filteredRecords.length >= 1) {
        return infoNotification(t('student.student.89'));
      }
      setPaymentRecords(paymentRecords.filter(paymentRecord => paymentRecord.paymentNo !== selectedRecord.paymentNo));
    } else {
      const minMonthSeq = _.min(
        data
          .filter(item => item.paymentTargetCode === selectedRecord.paymentTargetCode)
          .map(item => item.monthSeq),
      );
      const filteredRecords = paymentRecords.filter(
        item => {
          return (
            item.monthSeq < selectedRecord.monthSeq &&
            item.paymentTargetCode === selectedRecord.paymentTargetCode &&
            item.paymentNo !== selectedRecord.paymentNo
          );
        },
      );
      if (
        (filteredRecords.length === 0 && selectedRecord.monthSeq !== minMonthSeq) ||
        (minMonthSeq && selectedRecord.monthSeq - minMonthSeq >= 2 && filteredRecords.length < 2)
      ) {
        return infoNotification(t('student.student.89'));
      }
      setPaymentRecords([...paymentRecords, selectedRecord]);
    }
  };

  const handleInstallmentChange = (paymentNo: number, value?: string) => {
    console.log(paymentNo);
    console.log(value);
    const targetIndex = paymentInstallments.findIndex(
      item => item.paymentNo === paymentNo,
    );

    if (targetIndex !== -1) {
      // 기존 항목 업데이트
      const updatedInstallments = [...paymentInstallments];
      if (value) {
        updatedInstallments[targetIndex] = {
          ...updatedInstallments[targetIndex],
          value,
        };
      } else {
        updatedInstallments[targetIndex] = {
          paymentNo: 0,
          value: '',
        };
      }

      setPaymentInstallments(updatedInstallments);
    } else if (value) {
      // 항목이 없을 경우 새로 추가
      setPaymentInstallments([
        ...paymentInstallments,
        { paymentNo, value },
      ]);
    }
  };

  const handleCouponChange = (paymentNo: number, couponCodes: string[]) => {
    const selectedCouponCodes = couponCodes.map(code => parseInt(code));
    let updateCoupons = [...selectedCoupons];

    // 1. 현재 payment에 연결된 기존 쿠폰 중에서 선택 안 된 쿠폰은 해제 (paymentNo = 0)
    updateCoupons = updateCoupons.map(coupon => {
      if (
        coupon.paymentNo === paymentNo &&
        !selectedCouponCodes.includes(coupon.couponStudentCode)
      ) {
        return { ...coupon, paymentNo: 0 }; // 선택 해제된 것
      }
      return coupon;
    });

    // 2. 선택된 쿠폰이 다른 payment에 연결되어 있다면 해제 후, 현재 payment에 재등록
    selectedCouponCodes.forEach(code => {
      const origin = selectedCoupons.find(c => c.couponStudentCode === code);
      if (!origin) return;

      // 다른 payment에 묶여 있다면 해제
      updateCoupons = updateCoupons.map(c =>
        c.couponStudentCode === code ? { ...c, paymentNo } : c,
      );

      // selectedCoupons에 없던 신규 쿠폰일 경우 추가
      const exists = updateCoupons.find(c => c.couponStudentCode === code);
      if (!exists) {
        updateCoupons.push({ ...origin, paymentNo });
      }
    });
    setSelectedCoupons(updateCoupons);

    console.log(updateCoupons);
  };

  function getMonthName(monthCode?: string): string {
    monthCode = monthCode?.trim();

    const monthNames: { [key: string]: string } = {
      '1': 'Jan',
      '2': 'Feb',
      '3': 'Mar',
      '4': 'Apr',
      '5': 'May',
      '6': 'Jun',
      '7': 'Jul',
      '8': 'Aug',
      '9': 'Sep',
      '10': 'Oct',
      '11': 'Nov',
      '12': 'Dec',
    };

    // 월 코드가 존재하고 유효한 경우 해당 월을 반환, 그렇지 않으면 "ETC" 반환
    return monthCode ? monthNames[monthCode] : 'ETC';
  }

  const getButtonStatusClass = (paymentNo?: number): string => {
    const payment = paymentStatus.find(item => item.paymentNo === paymentNo);

    if (!payment) {
      return ''; // paymentNo에 해당하는 항목이 없으면 빈 문자열 반환
    }

    // 상태에 따라 버튼 클래스 반환
    switch (payment.status) {
      case 'FA':
        return 'btn-fail';  // 실패한 경우
      case 'RE':
        return 'wait';  // 대기 중인 경우
      case 'SU':
        return 'btn-success';  // 완료한 경우
      default:
        return '';
    }
  };

  const getTextStatusClass = (paymentNo?: number): string => {
    const payment = paymentStatus.find(item => item.paymentNo === paymentNo);

    if (!payment) {
      return ''; // paymentNo에 해당하는 항목이 없으면 빈 문자열 반환
    }

    // 상태에 따라 텍스트 반환
    switch (payment.status) {
      case 'FA':
        return '결제실패';  // 실패한 경우
      case 'RE':
        return '결제대기';  // 대기 중인 경우
      case 'SU':
        return '결제완료';  // 완료한 경우
      default:
        return '';
    }
  };

  const getPaymentEditAuth = (paymentTargetCode: string) => {
    if(paymentTargetCode === 'PT') {
      return false;
    }

    //본사 운영 권한(학사팀)
    if(PAYMENT_EDIT_PERMISSIONS.PAYMENT_EDIT_HQ_MANAGER.includes(auth.groupNo)) {
      return true;
    }

    //셔틀비는 누구나 수정 가능하도록 처리 20260129
    if(paymentTargetCode === 'ST') {
      return true;
    }

    //캠퍼스 운영 권한(원장, 상담팀장)
    if(PAYMENT_EDIT_PERMISSIONS.PAYMENT_EDIT_CAMPUS_MANAGER.includes(auth.groupNo)) {
      if(paymentTargetCode === 'SC' || paymentTargetCode === 'ET') {
        return true;
      }

      //수강료는 가맹 캠퍼스만 수정 가능 처리
      if(paymentTargetCode === 'TU') {
        console.log(campusData.find(item => item.campusCode === currentCampus)?.campusDiv);
        if(campusData.find(item => item.campusCode === currentCampus)?.campusDiv === 'BR') {
          return true;
        } else {
          return false;
        }
      }
    }

    return false;
  }

  return (
    <>
      <div className="comm-content__area">
        <div className="list-pay">
          {
            data.map(((paymentRecord, index) => (
              <>

                <Tooltip 
                  title={prStatusPayments.includes(paymentRecord.paymentNo) ? '해당 건은 결제 의심건입니다. 개발팀 문의 부탁드립니다.' : ''}
                  placement="top"
                >
                  <div className={`item-pay ${prStatusPayments.includes(paymentRecord.paymentNo) ? 'pr-disabled' : ''}`}>
                  <Checkbox
                    checked={isPaymentRecordChecked(paymentRecord.paymentNo) && !prStatusPayments.includes(paymentRecord.paymentNo)}
                    onChange={() => togglePaymentRecord(paymentRecord)}
                    disabled={(() => {
                      return prStatusPayments.includes(paymentRecord.paymentNo);
                    })()}
                  />

                  <div className="profile">
                    <div className={`thumb ${!!paymentRecord.monthNameCode && 'mint'}`}>
                      {getMonthName(paymentRecord.monthNameCode)}
                    </div>

                    <div className="meta">
                      <strong
                        className="name">{paymentRecord.semesterName}({paymentRecord.className}) {getCommonCodeLabel('PA0001', paymentRecord.paymentTargetCode?.trim())}</strong>
                      <span className="date">
                          납입기한 {getDateFormat(paymentRecord.paymentCloseDate)}

                        {prStatusPayments.includes(paymentRecord.paymentNo) ? (
                          <span className={`status btn-processing`}>
                            <span style={{ color: 'red' }}> ⚠️ 결제 의심</span>
                          </span>
                        ) : paymentStatus.find(it => it.paymentNo === paymentRecord.paymentNo) ? (
                          <span className={`status ${getButtonStatusClass(paymentRecord.paymentNo)}`}>
                            {getTextStatusClass(paymentRecord.paymentNo)}
                          </span>
                        ) : (
                          <span className={`status wait`}>
                            결제대기
                          </span>
                        )}
                        </span>
                    </div>
                  </div>

                  <Select
                    mode="multiple"
                    maxTagCount={1}
                    className="w-140"
                    style={{minWidth: '140px'}}
                    placeholder="쿠폰 선택"
                    value={selectedCoupons
                      .filter(c => c.paymentNo === paymentRecord.paymentNo)
                      .map(c => String(c.couponStudentCode))}
                    onChange={(values: string[]) =>
                      handleCouponChange(paymentRecord.paymentNo, values)
                    }
                  >
                    {selectedCoupons
                      .filter(coupon => coupon.paymentTargetCode === paymentRecord.paymentTargetCode)
                      .map((coupon, idx) => (
                        <Select.Option
                          key={`${coupon.couponStudentCode}-coupon-${idx}`}
                          value={String(coupon.couponStudentCode)}
                        >
                          {coupon.couponName}
                        </Select.Option>
                      ))}
                  </Select>


                  <div className="input-form col1">
                    <label htmlFor="item1">할인</label>
                    <input type="text" id="item1" className="form-control"
                           value={getMaxAmount(paymentRecord.price, getDiscountAmount(paymentRecord.discountAmount, index))?.toLocaleString()}
                           placeholder="" />
                  </div>

                  <div className="input-form col2">
                    <label htmlFor="item1">납부금액</label>
                    <input type="text" id="item1" className="form-control"
                           value={formatAmount(String(paymentAmounts[index]?.amount))} placeholder="" />
                  </div>

                  <Select
                    className="w-72"
                    placeholder={paymentAmounts[index]?.amount < 50000 ? '일시불' : '할부'}
                    value={paymentInstallments.find(
                      it => it.paymentNo === paymentRecord.paymentNo,
                    )?.value}
                    disabled={paymentAmounts[index]?.amount < 50000}
                    onChange={(value: string) => handleInstallmentChange(paymentRecord.paymentNo, value)}
                  >
                    {selectInstallment.map((text, idx) => (
                      <Select.Option
                        key={`${paymentRecord.paymentNo}-installment-${idx}`}
                        value={text}
                      >
                        {text}
                      </Select.Option>
                    ))}
                  </Select>

                  <div className="total">
                    <b>{paymentRecord.price.toLocaleString()}원</b>
                  </div>

                  <div className="area-btn">
                    {
                      (getPaymentEditAuth(paymentRecord.paymentTargetCode))
                      &&
                      (
                        <Button
                          className="btn-basic pink-line"
                          onClick={() => {
                            setPaymentRecord(paymentRecord);
                            if (paymentRecord.paymentTargetCode === 'SC') setSpecialClassAmountVisible(true);
                            if (paymentRecord.paymentTargetCode === 'ST') setShuttleAmountVisible(true);
                            if (paymentRecord.paymentTargetCode === 'TU') { setPaymentAmountVisible(true);}
                            if (paymentRecord.paymentTargetCode === 'PT') { setPaymentAmountVisible(true);}
                            if (paymentRecord.paymentTargetCode === 'ET') { setPaymentAmountVisible(true);}
                            if (paymentRecord.paymentTargetCode === 'ON') { setPaymentAmountVisible(true);}
                            if (paymentRecord.paymentTargetCode === 'AL') { setPaymentAmountVisible(true);}
                          }}
                          text={t('수정')}
                        />

                      )
                    }
                  </div>
                  </div>
                </Tooltip>
              </>
            )))
          }
        </div>

        <div className="comm-table__area rowType">
          <table>
            <colgroup>
              <col width="13%" />
              <col width="20%" />
              <col width="13%" />
              <col width="20%" />
              <col width="13%" />
              <col width="20%" />
            </colgroup>
            <tbody>
              <tr>
                <th>선택금액</th>
                <td><b>{paymentAmounts.filter(item => item.checked).reduce((sum, item) => sum + item.price, 0).toLocaleString()}원</b></td>
                <th>할인금액</th>
                <td><b>{paymentAmounts.filter(item => item.checked).reduce((sum, item, idx) => sum + getDiscountAmount(data[idx].discountAmount, idx), 0).toLocaleString()}원</b></td>
                <th>합계금액</th>
                <td><b>{paymentAmounts.filter(item => item.checked).reduce((sum, item) => sum + item.amount, 0).toLocaleString()}원</b></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {shuttleAmountVisible && (
        <ShuttleAmountModal
          student={student}
          visible={shuttleAmountVisible}
          setVisible={setShuttleAmountVisible}
          record={paymentRecord}
          doGetStudentPayments={() => {
            doGetStudentPayments();
            handleInstallmentChange(paymentRecord!.paymentNo, undefined);
          }}
        />
      )}

      {specialClassAmountVisible && (
        <SpecialClassAmountModal
          student={student}
          visible={specialClassAmountVisible}
          setVisible={setSpecialClassAmountVisible}
          record={paymentRecord}
          doGetStudentPayments={() => {
            doGetStudentPayments();
            handleInstallmentChange(paymentRecord!.paymentNo, undefined);
          }}
        />
      )}

      {paymentAmountVisible && (
        <PaymentAmountModal
          visible={paymentAmountVisible}
          setVisible={setPaymentAmountVisible}
          record={paymentRecord}
          doGetStudentPayments={() => {
            doGetStudentPayments();
            handleInstallmentChange(paymentRecord!.paymentNo, undefined);
          }}
        />
      )}
    </>
  );
};

export default PaymentProcessingTable;